import { type Room } from 'livekit-client'

import { findNearestText } from 'modules/ai_voice/lib/observers/find-text'
import { performRpc } from 'modules/ai_voice/lib/rpc'

const IDLE_MS = 1000

const SENSITIVE_FIELDS = [
  'password',
  'passcode',
  'secret',
  'token',
  'otp',
  'pin',
  'cvv',
  'ssn',
]

let timeoutId: number | null = null
let currentInput: HTMLInputElement | HTMLTextAreaElement | null = null
let handleInput: ((event: Event) => void) | null = null
let handleFocusOut: ((event: FocusEvent) => void) | null = null

function isSensitiveInput(input: HTMLInputElement | HTMLTextAreaElement) {
  const descriptors = [
    input.type,
    input.name,
    input.id,
    input.autocomplete,
    input.getAttribute('aria-label') ?? '',
    input.getAttribute('placeholder') ?? '',
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

  return SENSITIVE_FIELDS.some((field) => descriptors.includes(field))
}

export function registerTypeObserver(room: Room): void {
  function notifyFieldTyped(text: string, field: string) {
    performRpc(room, 'fieldTyped', { text, field }).catch(() => {})
  }

  function flushCurrentInput() {
    if (!currentInput) {
      return
    }

    const input = currentInput
    const text = input.value.trim()
    currentInput = null
    timeoutId = null

    if (text) {
      const field = findNearestText(input)
      const value = isSensitiveInput(input) ? '***' : text
      notifyFieldTyped(value, field)
    }
  }

  handleInput = (event) => {
    if (
      !event.isTrusted ||
      (!(event.target instanceof HTMLInputElement) &&
        !(event.target instanceof HTMLTextAreaElement))
    ) {
      return
    }

    currentInput = event.target

    if (timeoutId) {
      window.clearTimeout(timeoutId)
    }

    timeoutId = window.setTimeout(() => {
      flushCurrentInput()
    }, IDLE_MS)
  }

  handleFocusOut = (event) => {
    if (
      !event.isTrusted ||
      (!(event.target instanceof HTMLInputElement) &&
        !(event.target instanceof HTMLTextAreaElement)) ||
      event.target !== currentInput
    ) {
      return
    }

    if (timeoutId) {
      window.clearTimeout(timeoutId)
    }

    flushCurrentInput()
  }

  document.addEventListener('input', handleInput, true)
  document.addEventListener('focusout', handleFocusOut, true)
}

export function unregisterTypeObserver(): void {
  if (timeoutId) {
    window.clearTimeout(timeoutId)
    timeoutId = null
  }

  if (handleInput) {
    document.removeEventListener('input', handleInput, true)
    handleInput = null
  }

  if (handleFocusOut) {
    document.removeEventListener('focusout', handleFocusOut, true)
    handleFocusOut = null
  }

  currentInput = null
}
